import React, { useState } from 'react';
import {
  Dialog, DialogContent, Box, Typography, Checkbox, TextField,
  Chip, Divider, IconButton, Slide, useMediaQuery, useTheme,
  CircularProgress, InputAdornment,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  CalendarMonth as CalendarIcon,
  Remove as MinusIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import {
  formatCurrency,
  formatDate,
  calcularTotalComisiones,
  calcularTotalEgresos
} from '../../utils/liquidacionHelpers';

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const TIPO_CONFIG = {
  COMISION: { label: 'Comisión', color: '#1A237E', bg: '#E8EAF6' },
  SUELDO: { label: 'Sueldo', color: '#2E7D32', bg: '#E8F5E9' },
  JORNAL: { label: 'Jornal', color: '#E65100', bg: '#FFF3E0' },
};

const DialogGenerarLiquidacion = ({
  open, onClose, trabajador,
  serviciosDetalle = [], jornalesDetalle = [], egresosDetalle = [],
  liquidacionForm, setLiquidacionForm,
  onGenerar, loading, simboloMoneda = 'S/'
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const tipoPago = trabajador?.tipo_pago || 'COMISION';
  const tipoCfg = TIPO_CONFIG[tipoPago] || TIPO_CONFIG.COMISION;
  const permiteComision = tipoPago === 'COMISION' || trabajador?.permite_comision_extra;

  const montoSueldoMensual = parseFloat(trabajador?.monto_sueldo || 0);
  const montoJornalDia = parseFloat(trabajador?.monto_jornal || 0);
  const periodoPago = trabajador?.periodo_pago || 'MENSUAL';
  const montoSueldoPeriodo = periodoPago === 'QUINCENAL' ? montoSueldoMensual / 2 : montoSueldoMensual;
  const descuentoDia = Math.round((montoSueldoMensual / 30) * 100) / 100;

  // === COMISIONES ===
  const totalComisiones = calcularTotalComisiones(serviciosDetalle, liquidacionForm.servicios_seleccionados);
  const totalEgresos = calcularTotalEgresos(egresosDetalle, liquidacionForm.egresos_seleccionados);

  // === JORNAL: seleccionar días individuales ===
  const jornalesPorFecha = {};
  jornalesDetalle.forEach(j => {
    const key = new Date(j.fecha).toISOString().split('T')[0];
    if (!jornalesPorFecha[key]) jornalesPorFecha[key] = { fecha: key, monto: parseFloat(j.monto_jornal || 0), origen: j.origen };
  });
  const jornalesUnicos = Object.values(jornalesPorFecha);
  const jornalesSeleccionados = liquidacionForm.jornales_seleccionados || [];
  const totalJornales = jornalesSeleccionados.reduce((sum, fecha) => {
    const j = jornalesPorFecha[fecha];
    return sum + (j ? j.monto : montoJornalDia);
  }, 0);

  // === SUELDO: sueldo del periodo - descuento por faltas ===
  const diasDescuento = parseInt(liquidacionForm.dias_descuento || 0);
  const totalDescuentoFaltas = diasDescuento * descuentoDia;
  const totalSueldo = montoSueldoPeriodo - totalDescuentoFaltas;

  // === TOTALES ===
  let totalBruto = 0;
  if (tipoPago === 'SUELDO') totalBruto = totalSueldo + totalComisiones;
  else if (tipoPago === 'JORNAL') totalBruto = totalJornales + totalComisiones;
  else totalBruto = totalComisiones;

  const totalNeto = totalBruto - totalEgresos;

  const hayAlgoSeleccionado = tipoPago === 'SUELDO'
    ? true // Sueldo siempre tiene algo (el sueldo base)
    : (liquidacionForm.servicios_seleccionados?.length > 0) || (jornalesSeleccionados.length > 0);

  // === DIAS DE ASISTENCIA registrados (para referencia del admin) ===
  const diasAsistencia = jornalesUnicos.length;

  // Toggles
  const toggleJornal = (fecha) => {
    const sel = jornalesSeleccionados.includes(fecha)
      ? jornalesSeleccionados.filter(f => f !== fecha)
      : [...jornalesSeleccionados, fecha];
    setLiquidacionForm({ ...liquidacionForm, jornales_seleccionados: sel });
  };

  const toggleTodosJornales = () => {
    const todos = jornalesSeleccionados.length === jornalesUnicos.length;
    setLiquidacionForm({ ...liquidacionForm, jornales_seleccionados: todos ? [] : jornalesUnicos.map(j => j.fecha) });
  };

  const toggleEgreso = (id) => {
    const sel = liquidacionForm.egresos_seleccionados.includes(id)
      ? liquidacionForm.egresos_seleccionados.filter(e => e !== id)
      : [...liquidacionForm.egresos_seleccionados, id];
    setLiquidacionForm({ ...liquidacionForm, egresos_seleccionados: sel });
  };

  return (
    <Dialog
      open={open} onClose={onClose}
      fullScreen={isMobile} maxWidth="sm" fullWidth
      TransitionComponent={isMobile ? Transition : undefined}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3, maxHeight: isMobile ? '100%' : '92vh' } }}
    >
      {/* Header */}
      <Box sx={{
        background: `linear-gradient(135deg, ${tipoCfg.color}, ${tipoCfg.color}CC)`,
        color: '#fff', px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 800 }}>
            Liquidar — {trabajador?.nombre_trabajador} {trabajador?.apellido_trabajador}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <Chip label={tipoCfg.label} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700, fontSize: 11 }} />
            {tipoPago === 'JORNAL' && <Typography sx={{ fontSize: 12, opacity: 0.8 }}>{simboloMoneda} {montoJornalDia.toFixed(2)}/día</Typography>}
            {tipoPago === 'SUELDO' && <Typography sx={{ fontSize: 12, opacity: 0.8 }}>{simboloMoneda} {montoSueldoMensual.toFixed(2)}/mes · {periodoPago.toLowerCase()}</Typography>}
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* ========== SUELDO: Card de sueldo con descuento por faltas ========== */}
          {tipoPago === 'SUELDO' && (
            <Box sx={{ border: '1px solid #A5D6A7', borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{ bgcolor: '#E8F5E9', px: 2, py: 1.5, borderBottom: '1px solid #C8E6C9' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#2E7D32' }}>
                  Sueldo {periodoPago === 'QUINCENAL' ? 'Quincenal' : 'Mensual'}
                </Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                {/* Sueldo del periodo */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1E293B' }}>
                      Sueldo {periodoPago === 'QUINCENAL' ? 'quincenal' : 'mensual'}
                    </Typography>
                    {periodoPago === 'QUINCENAL' && (
                      <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>
                        {simboloMoneda} {montoSueldoMensual.toFixed(2)}/mes ÷ 2
                      </Typography>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#2E7D32' }}>
                    {simboloMoneda} {montoSueldoPeriodo.toFixed(2)}
                  </Typography>
                </Box>

                {/* Referencia de asistencia — compacta con scroll */}
                <Box sx={{ bgcolor: '#F0FFF4', borderRadius: 2, p: 1.5, mb: 1.5, border: '1px solid #C8E6C9' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: diasAsistencia > 0 ? 0.5 : 0 }}>
                    <Typography sx={{ fontSize: 12, color: '#2E7D32', fontWeight: 600 }}>
                      Asistencia registrada
                    </Typography>
                    <Chip label={`${diasAsistencia} día${diasAsistencia !== 1 ? 's' : ''}`} size="small"
                      sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: '#2E7D32', color: '#fff' }} />
                  </Box>
                  {diasAsistencia > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3, mt: 0.5, maxHeight: 60, overflow: 'auto' }}>
                      {jornalesUnicos.map(j => (
                        <Chip key={j.fecha} size="small" label={
                          new Date(j.fecha + 'T12:00:00').toLocaleDateString('es-PE', { day: '2-digit' })
                        } sx={{ height: 18, fontSize: 10, minWidth: 28, bgcolor: '#C8E6C9', color: '#2E7D32', fontWeight: 700 }} />
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Descuento por faltas */}
                <Box sx={{
                  bgcolor: diasDescuento > 0 ? '#FFF3E0' : '#F8FAFC',
                  borderRadius: 2, p: 1.5, border: '1px solid',
                  borderColor: diasDescuento > 0 ? '#FFE082' : '#E2E8F0',
                }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1E293B', mb: 1 }}>
                    ¿Cuántos días descontar por falta?
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconButton size="small"
                      onClick={() => setLiquidacionForm({ ...liquidacionForm, dias_descuento: Math.max(0, diasDescuento - 1) })}
                      disabled={diasDescuento <= 0}
                      sx={{ bgcolor: '#E2E8F0', width: 32, height: 32 }}>
                      <MinusIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <TextField
                      size="small" type="number" value={diasDescuento}
                      onChange={(e) => setLiquidacionForm({ ...liquidacionForm, dias_descuento: Math.max(0, parseInt(e.target.value) || 0) })}
                      sx={{ width: 70, '& .MuiInputBase-root': { borderRadius: 2, textAlign: 'center' }, '& input': { textAlign: 'center', fontWeight: 800, fontSize: 18 } }}
                      inputProps={{ min: 0 }}
                    />
                    <IconButton size="small"
                      onClick={() => setLiquidacionForm({ ...liquidacionForm, dias_descuento: diasDescuento + 1 })}
                      sx={{ bgcolor: '#E2E8F0', width: 32, height: 32 }}>
                      <AddIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    {diasDescuento > 0 && (
                      <Typography sx={{ fontSize: 13, color: '#EF4444', fontWeight: 700 }}>
                        -{simboloMoneda} {totalDescuentoFaltas.toFixed(2)}
                      </Typography>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 11, color: '#94A3B8', mt: 0.5 }}>
                    Cada día: {simboloMoneda} {descuentoDia.toFixed(2)} (sueldo ÷ 30)
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* ========== JORNAL: seleccionar días a pagar ========== */}
          {tipoPago === 'JORNAL' && (
            <Box sx={{ border: '1px solid #FFCC80', borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
                bgcolor: '#FFF3E0', borderBottom: '1px solid #FFE0B2',
              }}>
                <CalendarIcon sx={{ fontSize: 18, color: '#E65100' }} />
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#E65100', flex: 1 }}>
                  Jornales pendientes ({jornalesUnicos.length})
                </Typography>
                {jornalesUnicos.length > 0 && (
                  <Checkbox
                    size="small"
                    checked={jornalesSeleccionados.length === jornalesUnicos.length && jornalesUnicos.length > 0}
                    indeterminate={jornalesSeleccionados.length > 0 && jornalesSeleccionados.length < jornalesUnicos.length}
                    onChange={toggleTodosJornales}
                    sx={{ p: 0, color: '#E65100', '&.Mui-checked': { color: '#E65100' } }}
                  />
                )}
              </Box>
              <Box sx={{ maxHeight: 200, overflow: 'auto', px: 1.5, py: 1 }}>
                {jornalesUnicos.length === 0 ? (
                  <Typography sx={{ fontSize: 12, color: '#94A3B8', textAlign: 'center', py: 2 }}>
                    Sin jornales pendientes — marca asistencia primero
                  </Typography>
                ) : jornalesUnicos.map(j => (
                  <Box key={j.fecha} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderBottom: '1px solid #FFF8E1' }}>
                    <Checkbox size="small" checked={jornalesSeleccionados.includes(j.fecha)}
                      onChange={() => toggleJornal(j.fecha)}
                      sx={{ p: 0, color: '#E65100', '&.Mui-checked': { color: '#E65100' } }} />
                    <Typography sx={{ fontSize: 13, color: '#1E293B', flex: 1 }}>
                      {new Date(j.fecha + 'T12:00:00').toLocaleDateString('es-PE', { weekday: 'short', day: '2-digit', month: 'short' })}
                    </Typography>
                    <Chip label={j.origen === 'MANUAL' ? 'Admin' : j.origen === 'APP' ? 'App' : 'Servicio'} size="small" sx={{
                      height: 18, fontSize: 9, fontWeight: 700,
                      bgcolor: j.origen === 'MANUAL' ? '#FFF3E0' : j.origen === 'APP' ? '#F3E5F5' : '#E3F2FD',
                      color: j.origen === 'MANUAL' ? '#E65100' : j.origen === 'APP' ? '#7B1FA2' : '#1565C0',
                    }} />
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#E65100' }}>
                      {simboloMoneda} {montoJornalDia.toFixed(2)}
                    </Typography>
                  </Box>
                ))}
              </Box>
              {jornalesSeleccionados.length > 0 && (
                <Box sx={{ px: 1.5, py: 1, bgcolor: '#FFF3E0', borderTop: '1px solid #FFE0B2', display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748B' }}>
                    {jornalesSeleccionados.length} día{jornalesSeleccionados.length !== 1 ? 's' : ''} × {simboloMoneda} {montoJornalDia.toFixed(2)}
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#E65100' }}>
                    {simboloMoneda} {totalJornales.toFixed(2)}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* ========== COMISIONES ========== */}
          {permiteComision && serviciosDetalle.length > 0 && (
            <Box sx={{ border: '1px solid #A5D6A7', borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, bgcolor: '#E8F5E908', borderBottom: '1px solid #C8E6C9' }}>
                <CheckIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1E293B', flex: 1 }}>
                  {tipoPago !== 'COMISION' ? 'Comisiones Extra' : 'Servicios'} ({serviciosDetalle.length})
                </Typography>
                <Checkbox size="small"
                  checked={liquidacionForm.servicios_seleccionados.length === serviciosDetalle.length}
                  indeterminate={liquidacionForm.servicios_seleccionados.length > 0 && liquidacionForm.servicios_seleccionados.length < serviciosDetalle.length}
                  onChange={() => {
                    const todos = liquidacionForm.servicios_seleccionados.length === serviciosDetalle.length;
                    setLiquidacionForm({ ...liquidacionForm, servicios_seleccionados: todos ? [] : serviciosDetalle.map(s => s.id_asignacion) });
                  }}
                  sx={{ p: 0, color: '#4CAF50', '&.Mui-checked': { color: '#4CAF50' } }}
                />
              </Box>
              <Box sx={{ maxHeight: 200, overflow: 'auto', px: 1.5, py: 1 }}>
                {serviciosDetalle.map(s => (
                  <Box key={s.id_asignacion} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderBottom: '1px solid #F8FAFC' }}>
                    <Checkbox size="small"
                      checked={liquidacionForm.servicios_seleccionados.includes(s.id_asignacion)}
                      onChange={(e) => {
                        const sel = e.target.checked
                          ? [...liquidacionForm.servicios_seleccionados, s.id_asignacion]
                          : liquidacionForm.servicios_seleccionados.filter(id => id !== s.id_asignacion);
                        setLiquidacionForm({ ...liquidacionForm, servicios_seleccionados: sel });
                      }}
                      sx={{ p: 0, color: '#4CAF50', '&.Mui-checked': { color: '#4CAF50' } }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1E293B' }} noWrap>
                        #{s.id_orden_servicio} — {s.nombre_servicio || s.nombre_cliente}
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: '#94A3B8' }}>{formatDate(s.fecha_orden)}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#4CAF50' }}>{formatCurrency(s.comision_total)}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* ========== EGRESOS ========== */}
          {egresosDetalle.length > 0 && (
            <Box sx={{ border: '1px solid #EF9A9A', borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{ px: 1.5, py: 1, bgcolor: '#FFEBEE', borderBottom: '1px solid #EF9A9A' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#C62828' }}>
                  Egresos / Descuentos ({egresosDetalle.length})
                </Typography>
              </Box>
              <Box sx={{ maxHeight: 150, overflow: 'auto', px: 1.5, py: 1 }}>
                {egresosDetalle.map(e => (
                  <Box key={e.id_egreso} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderBottom: '1px solid #FFF5F5' }}>
                    <Checkbox size="small"
                      checked={liquidacionForm.egresos_seleccionados.includes(e.id_egreso)}
                      onChange={() => toggleEgreso(e.id_egreso)}
                      sx={{ p: 0, color: '#EF4444', '&.Mui-checked': { color: '#EF4444' } }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1E293B' }}>{e.tipo_egreso}: {e.concepto}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#EF4444' }}>-{formatCurrency(e.monto)}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* ========== RESUMEN ========== */}
          <Box sx={{ p: 2, borderRadius: 3, bgcolor: '#F0F4FF', border: '1px solid #C7D2FE' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1A237E', mb: 1, textTransform: 'uppercase' }}>
              Resumen
            </Typography>
            {tipoPago === 'SUELDO' && (
              <>
                <Row label={`Sueldo ${periodoPago === 'QUINCENAL' ? 'quincenal' : 'mensual'}`}
                  value={`${simboloMoneda} ${montoSueldoPeriodo.toFixed(2)}`} color="#2E7D32" />
                {diasDescuento > 0 && (
                  <Row label={`Descuento ${diasDescuento} falta${diasDescuento > 1 ? 's' : ''}`}
                    value={`-${simboloMoneda} ${totalDescuentoFaltas.toFixed(2)}`} color="#EF4444" />
                )}
              </>
            )}
            {tipoPago === 'JORNAL' && totalJornales > 0 && (
              <Row label={`Jornales (${jornalesSeleccionados.length}d)`}
                value={`${simboloMoneda} ${totalJornales.toFixed(2)}`} color="#E65100" />
            )}
            {totalComisiones > 0 && (
              <Row label={`Comisiones (${liquidacionForm.servicios_seleccionados.length})`}
                value={`${simboloMoneda} ${totalComisiones.toFixed(2)}`} color="#4CAF50" />
            )}
            {totalEgresos > 0 && (
              <Row label={`Descuentos (${liquidacionForm.egresos_seleccionados.length})`}
                value={`-${simboloMoneda} ${totalEgresos.toFixed(2)}`} color="#EF4444" />
            )}
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#1A237E' }}>TOTAL NETO</Typography>
              <Typography sx={{ fontSize: 20, fontWeight: 800, color: totalNeto >= 0 ? '#1A237E' : '#EF4444' }}>
                {simboloMoneda} {totalNeto.toFixed(2)}
              </Typography>
            </Box>
          </Box>

          <TextField fullWidth size="small" label="Observaciones (opcional)" multiline rows={2}
            value={liquidacionForm.observaciones}
            onChange={(e) => setLiquidacionForm({ ...liquidacionForm, observaciones: e.target.value })}
            sx={{ '& .MuiInputBase-root': { borderRadius: 2 } }} />
        </Box>
      </DialogContent>

      {/* Footer */}
      <Box sx={{ borderTop: '1px solid #E2E8F0', p: 2, display: 'flex', gap: 1.5 }}>
        <Box onClick={onClose} sx={{
          flex: 1, py: 1.2, borderRadius: 2.5, textAlign: 'center',
          border: '1.5px solid #E2E8F0', cursor: 'pointer',
          fontSize: 14, fontWeight: 600, color: '#64748B',
          '&:hover': { bgcolor: '#F8FAFC' },
        }}>Cancelar</Box>
        <Box
          onClick={loading || !hayAlgoSeleccionado ? undefined : onGenerar}
          sx={{
            flex: 1.5, py: 1.2, borderRadius: 2.5, textAlign: 'center',
            background: !hayAlgoSeleccionado || loading ? '#CBD5E1' : 'linear-gradient(135deg, #10B981, #059669)',
            cursor: !hayAlgoSeleccionado || loading ? 'default' : 'pointer',
            fontSize: 14, fontWeight: 700, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
          }}
        >
          {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : null}
          {loading ? 'Generando...' : `Liquidar · ${simboloMoneda} ${totalNeto.toFixed(2)}`}
        </Box>
      </Box>
    </Dialog>
  );
};

const Row = ({ label, value, color }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.3 }}>
    <Typography sx={{ fontSize: 13, color: '#64748B' }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 700, color }}>{value}</Typography>
  </Box>
);

export default DialogGenerarLiquidacion;
